import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AiOutlineLeft, AiOutlineRight, AiOutlineDelete, AiOutlineEdit, AiOutlineCheck, AiOutlineClose } from "react-icons/ai";
import styles from "./styles/ComentariosModal.module.css";
import { PostagemDTO } from "../types";
import { useAuth } from "../../auth/AuthProvider";

interface ComentarioDTO {
    id: number;
    texto: string;
    criadoEm: string;
    usuarioId: number;
    usuarioNome: string;
    usuarioFotoUrl?: string;
}

interface ComentariosModalProps {
    postagemId: number;
    show: boolean;
    onClose: () => void;
    onChangeTotal?: (novoTotal: number) => void;
}

const BASE_URL = process.env.REACT_APP_API_URL;

const ComentariosModal: React.FC<ComentariosModalProps> = ({
    postagemId,
    show,
    onClose,
    onChangeTotal,
}) => {
    const [comentarios, setComentarios] = useState<ComentarioDTO[]>([]);
    const [novoComentario, setNovoComentario] = useState("");
    const [carregando, setCarregando] = useState(false);
    const [postagem, setPostagem] = useState<PostagemDTO | null>(null);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [editandoId, setEditandoId] = useState<number | null>(null);
    const [textoEditado, setTextoEditado] = useState("");

    const { user } = useAuth();
    const [mostrarMenu, setMostrarMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null); // 🔹 ref para clique fora
    const navigate = useNavigate();

    const arquivoAtual = postagem?.arquivos?.[currentFileIndex];
    const urlCompleta = arquivoAtual ? `${BASE_URL}${arquivoAtual.url}` : "";
    const isVideo = arquivoAtual?.tipo?.startsWith("video");

    // Buscar comentários
    const fetchComentarios = async () => {
        try {
            const res = await axios.get<ComentarioDTO[]>(`${BASE_URL}/postagens/${postagemId}/comentarios`);
            setComentarios(res.data);
        } catch (err) {
            console.error("Erro ao carregar comentários", err);
        }
    };

    // Buscar postagem
    const fetchPostagem = async () => {
        try {
            const res = await axios.get<PostagemDTO>(`${BASE_URL}/postagens/${postagemId}`);
            setPostagem(res.data);
            setCurrentFileIndex(0);
        } catch (err) {
            console.error("Erro ao carregar postagem", err);
        }
    };

    useEffect(() => {
        if (show) {
            fetchComentarios();
            fetchPostagem();
        }
    }, [show]);

    // 🔹 Fecha o menu se clicar fora
    useEffect(() => {
        const handleClickFora = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMostrarMenu(false);
            }
        };

        if (mostrarMenu) {
            document.addEventListener("mousedown", handleClickFora);
        } else {
            document.removeEventListener("mousedown", handleClickFora);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickFora);
        };
    }, [mostrarMenu]);

    const handleComentar = async () => {
        if (!novoComentario.trim()) return;
        setCarregando(true);

        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(
                `${BASE_URL}/postagens/${postagemId}/comentarios`,
                { texto: novoComentario },
                {
                    headers: token
                        ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
                        : undefined,
                }
            );
            const novo = res.data;
            setComentarios((prev) => [...prev, novo]);
            setNovoComentario("");
            if (onChangeTotal) onChangeTotal(comentarios.length + 1);
        } catch (err) {
            console.error("Erro ao criar comentário", err);
        } finally {
            setCarregando(false);
        }
    };

    const handleDeleteComentario = async (comentarioId: number) => {
        if (!window.confirm("Tem certeza que deseja excluir este comentário?")) return;
        const token = localStorage.getItem("token");

        try {
            await axios.delete(`${BASE_URL}/postagens/comentarios/${comentarioId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setComentarios((prev) => prev.filter((c) => c.id !== comentarioId));
            if (onChangeTotal) onChangeTotal(comentarios.length - 1);
        } catch (err) {
            console.error("Erro ao excluir comentário", err);
        }
    };

    const handleEditarComentario = (comentario: ComentarioDTO) => {
        setEditandoId(comentario.id);
        setTextoEditado(comentario.texto);
    };

    const handleSalvarEdicao = async (comentarioId: number) => {
        if (!textoEditado.trim()) return;
        const token = localStorage.getItem("token");

        try {
            const res = await axios.put(
                `${BASE_URL}/postagens/comentarios/${comentarioId}`,
                { texto: textoEditado },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setComentarios((prev) =>
                prev.map((c) => (c.id === comentarioId ? res.data : c))
            );
            setEditandoId(null);
        } catch (err) {
            console.error("Erro ao editar comentário", err);
        }
    };

    const handleCancelarEdicao = () => {
        setEditandoId(null);
        setTextoEditado("");
    };

    const handleRedirecionarPorPacote = () => {
        if (!postagem!.pacoteId) return;
        const queryParams = new URLSearchParams();
        queryParams.set("pacoteId", String(postagem!.pacoteId));
        navigate(`/user/feed/explore?${queryParams.toString()}`);
    };

    const handleRedirecionarPorPonto = (ponto: string) => {
        if (!ponto) return;
        const queryParams = new URLSearchParams();
        queryParams.set("pontoNome", ponto);
        navigate(`/user/feed/explore?${queryParams.toString()}`);
    };

    const podeExcluir = (comentario: ComentarioDTO) =>
        user && (user.id === comentario.usuarioId || user.id === postagem?.usuarioId);

    const podeEditar = (comentario: ComentarioDTO) =>
        user && user.id === comentario.usuarioId;

    const prevFile = () => {
        if (!postagem?.arquivos) return;
        setCurrentFileIndex((prev) =>
            prev === 0 ? postagem.arquivos.length - 1 : prev - 1
        );
    };

    const nextFile = () => {
        if (!postagem?.arquivos) return;
        setCurrentFileIndex((prev) =>
            prev === postagem.arquivos.length - 1 ? 0 : prev + 1
        );
    };

    if (!show || !postagem) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                {/* Lado esquerdo → mídia da postagem */}
                <div className={styles.postagemArea}>
                    {arquivoAtual ? (
                        <div className={styles.sliderContainer}>
                            <div className={styles.fileWrapper}>
                                {isVideo ? (
                                    <video
                                        key={arquivoAtual.id}
                                        src={urlCompleta}
                                        controls
                                        muted
                                        loop
                                        className={styles.fileMedia}
                                    />
                                ) : (
                                    <img
                                        key={arquivoAtual.id}
                                        src={urlCompleta}
                                        alt={arquivoAtual.pontoInteresse || ""}
                                        className={styles.fileMedia}
                                    />
                                )}
                                {arquivoAtual.pontoInteresse && (
                                    <div className={styles.pontoOverlay}
                                        onClick={() => handleRedirecionarPorPonto(arquivoAtual.pontoInteresse!)}
                                    >
                                        📍 {arquivoAtual.pontoInteresse}
                                    </div>
                                )}
                            </div>

                            {postagem.arquivos.length > 1 && (
                                <>
                                    <button onClick={prevFile} className={`${styles.sliderButton} ${styles.left}`}>
                                        <AiOutlineLeft size={30} />
                                    </button>
                                    <button onClick={nextFile} className={`${styles.sliderButton} ${styles.right}`}>
                                        <AiOutlineRight size={30} />
                                    </button>
                                    <div className={styles.sliderIndicator}>
                                        {currentFileIndex + 1} / {postagem.arquivos.length}
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className={styles.semMidia}>Sem mídia</div>
                    )}
                </div>

                {/* Lado direito → comentários */}
                <div className={styles.comentariosArea}>
                    <div className={styles.header}>
                        <div className={styles.userInfo}>
                            <img
                                src={
                                    postagem.usuarioFotoUrl
                                        ? `${BASE_URL}${postagem.usuarioFotoUrl}`
                                        : "/default-user.png"
                                }
                                alt={postagem.usuarioNome}
                            />
                            <div className={styles.userText}>
                                <strong>{postagem.usuarioNome}</strong>

                                {postagem?.pacoteTitulo && (
                                    <div className={styles.pacoteTituloWrapper} ref={menuRef}>
                                        <p
                                            className={styles.pacoteTitulo}
                                            onClick={() => setMostrarMenu((prev) => !prev)}
                                        >
                                            📦 {postagem.pacoteTitulo}
                                        </p>

                                        {mostrarMenu && (
                                            <div className={styles.pacoteMenu}>
                                                <button
                                                    onClick={() => {
                                                        setMostrarMenu(false);
                                                        handleRedirecionarPorPacote();
                                                    }}
                                                >
                                                    Ver postagens do pacote
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setMostrarMenu(false);
                                                        navigate(`/flight-details/${postagem.pacoteId}`);
                                                    }}
                                                >
                                                    Ver página do pacote
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <button className={styles.closeBtn} onClick={onClose}>✕</button>
                    </div>

                    {postagem.conteudo && (
                        <div className={styles.postContent}>
                            <p>{postagem.conteudo}</p>
                        </div>
                    )}

                    <div className={styles.comentariosLista}>
                        {comentarios.length === 0 && <p className={styles.empty}>Nenhum comentário ainda.</p>}

                        {comentarios.map((c) => (
                            <div key={c.id} className={styles.comentario}>
                                <img
                                    src={
                                        c.usuarioFotoUrl
                                            ? `${BASE_URL}${c.usuarioFotoUrl}`
                                            : "/default-user.png"
                                    }
                                    alt={c.usuarioNome}
                                />
                                <div className={styles.comentarioContent}>
                                    <div className={styles.comentarioHeader}>
                                        <strong>{c.usuarioNome}</strong>
                                        <div className={styles.actionButtons}>
                                            {podeEditar(c) && editandoId !== c.id && (
                                                <button
                                                    className={styles.editBtn}
                                                    onClick={() => handleEditarComentario(c)}
                                                    title="Editar comentário"
                                                >
                                                    <AiOutlineEdit size={18} />
                                                </button>
                                            )}
                                            {podeExcluir(c) && editandoId !== c.id && (
                                                <button
                                                    onClick={() => handleDeleteComentario(c.id)}
                                                    className={styles.deleteBtn}
                                                    title="Excluir comentário"
                                                >
                                                    <AiOutlineDelete size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {editandoId === c.id ? (
                                        <div className={styles.edicaoArea}>
                                            <textarea
                                                value={textoEditado}
                                                onChange={(e) => setTextoEditado(e.target.value)}
                                                rows={2}
                                            />
                                            <div className={styles.edicaoBtns}>
                                                <button onClick={() => handleSalvarEdicao(c.id)} title="Salvar">
                                                    <AiOutlineCheck size={18} />
                                                </button>
                                                <button onClick={handleCancelarEdicao} title="Cancelar">
                                                    <AiOutlineClose size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <p>{c.texto}</p>
                                            <small>
                                                {new Date(c.criadoEm).toLocaleString("pt-BR", {
                                                    dateStyle: "short",
                                                    timeStyle: "short",
                                                })}
                                            </small>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Novo comentário */}
                    <div className={styles.newComment}>
                        <textarea
                            placeholder="Escreva um comentário..."
                            value={novoComentario}
                            onChange={(e) => setNovoComentario(e.target.value)}
                            rows={2}
                        />
                        <button onClick={handleComentar} disabled={carregando}>
                            {carregando ? "Enviando..." : "Comentar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ComentariosModal;